<template>
  <v-text-field
    ref="fieldRef"
    :model-value="displayValue"
    :label="label"
    :placeholder="placeholder"
    :aria-label="ariaLabel || label || placeholder"
    :density="density"
    :variant="variant"
    :hide-details="hideDetails"
    :readonly="readonly || locked"
    :disabled="disabled"
    :style="fieldStyle"
    :prepend-inner-icon="prependIcon"
    :clearable="clearable && !locked && !disabled"
    :class="fieldClass"
    single-line
    @click.stop
    @mousedown.stop
    @click:control="openPicker"
    @click:prepend-inner="openPicker"
    @click:clear.stop="clearValue"
    @update:model-value="onTyped"
    @keyup.enter.stop="$emit('enter')"
  />
</template>

<script setup>
import { nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'

const props = defineProps({
  modelValue: { type: String, default: '' },
  mode: { type: String, default: 'date' }, // date | datetime | time
  label: { type: String, default: '' },
  placeholder: { type: String, default: '' },
  ariaLabel: { type: String, default: '' },
  density: { type: String, default: 'compact' },
  variant: { type: String, default: 'outlined' },
  hideDetails: { type: [Boolean, String], default: true },
  readonly: { type: Boolean, default: false },
  locked: { type: Boolean, default: false },
  disabled: { type: Boolean, default: false },
  clearable: { type: Boolean, default: true },
  prependIcon: { type: String, default: 'mdi-calendar' },
  fieldStyle: { type: [String, Object], default: undefined },
  fieldClass: { type: [String, Object, Array], default: undefined },
  dateFormat: { type: String, default: '' },
})

const emit = defineEmits(['update:modelValue', 'enter'])

const fieldRef = ref(null)
const displayValue = ref(props.modelValue || '')
let fp = null

function findInputEl() {
  const root = fieldRef.value?.$el || fieldRef.value
  return root?.querySelector?.('input') || null
}

function pickerOptions() {
  const opts = {
    allowInput: true,
    disableMobile: true,
    clickOpens: !props.locked && !props.readonly && !props.disabled,
    onChange(_selectedDates, dateStr) {
      displayValue.value = dateStr
      emit('update:modelValue', dateStr)
    },
  }

  if (props.mode === 'datetime') {
    opts.enableTime = true
    opts.time_24hr = true
    opts.dateFormat = props.dateFormat || 'Y-m-d H:i'
  } else if (props.mode === 'time') {
    opts.enableTime = true
    opts.noCalendar = true
    opts.time_24hr = true
    opts.dateFormat = props.dateFormat || 'H:i'
  } else {
    opts.dateFormat = props.dateFormat || 'Y-m-d'
  }

  return opts
}

function initPicker() {
  if (props.disabled) return
  const flatpickrFn = window.flatpickr || window.jQuery?.fn?.flatpickr
  if (!flatpickrFn && !window.jQuery) return

  const input = findInputEl()
  if (!input) return

  destroyPicker()

  if (window.jQuery && typeof window.jQuery(input).flatpickr === 'function') {
    fp = window.jQuery(input).flatpickr(pickerOptions())
  } else if (typeof window.flatpickr === 'function') {
    fp = window.flatpickr(input, pickerOptions())
  }

  if (fp && props.modelValue) {
    try {
      const instance = Array.isArray(fp) ? fp[0] : fp
      instance?.setDate?.(String(props.modelValue), false)
    } catch (_) {
      /* ignore parse errors */
    }
  }
}

function destroyPicker() {
  if (!fp) return
  try {
    if (Array.isArray(fp)) fp.forEach((p) => p?.destroy?.())
    else fp.destroy?.()
  } catch (_) {
    /* ignore */
  }
  fp = null
}

function openPicker() {
  if (props.locked || props.readonly || props.disabled) return
  if (!fp) initPicker()
  const instance = Array.isArray(fp) ? fp[0] : fp
  instance?.open?.()
}

function clearValue() {
  displayValue.value = ''
  emit('update:modelValue', '')
  const instance = Array.isArray(fp) ? fp[0] : fp
  try {
    instance?.clear?.()
  } catch (_) {
    /* ignore */
  }
}

function onTyped(val) {
  if (props.locked || props.readonly) return
  displayValue.value = val == null ? '' : String(val)
  emit('update:modelValue', displayValue.value)
}

watch(
  () => props.modelValue,
  (val) => {
    const next = val == null ? '' : String(val)
    displayValue.value = next
    if (fp) {
      const instance = Array.isArray(fp) ? fp[0] : fp
      try {
        instance?.setDate?.(next || null, false)
      } catch (_) {
        /* ignore */
      }
    }
  },
)

watch(
  () => [props.locked, props.readonly, props.disabled, props.mode],
  async () => {
    await nextTick()
    initPicker()
  },
)

onMounted(async () => {
  displayValue.value = props.modelValue == null ? '' : String(props.modelValue)
  await nextTick()
  initPicker()
  // flatpickr script may load after Vue mount on first paint
  setTimeout(initPicker, 100)
})

onBeforeUnmount(() => {
  destroyPicker()
})
</script>
